import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, Camera, Check, KeyRound, ShieldAlert, UserRound, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API, withCredentials: true });
const imgUrl = (src) => (src && src.startsWith("/api/") ? `${BACKEND}${src}` : src);

function Avatar({ user, size = 96 }) {
  const src = user?.profile_photo ? imgUrl(user.profile_photo) : "";
  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className="profile-avatar"
      style={{ height: size, width: size, fontSize: size * 0.35 }}
      data-testid="profile-avatar"
    >
      {src ? <img src={src} alt={user?.name || ""} /> : <b>{initials}</b>}
    </span>
  );
}

export function ProfileButton({ user, onClick }) {
  if (!user || user.role !== "customer") return null;
  return (
    <button className="text-btn" onClick={onClick} data-testid="profile-header-button">
      <Avatar user={user} size={22} />
      <span>My profile</span>
    </button>
  );
}

export default function ProfileModal({ user, setUser, sales, onClose }) {
  const [tab, setTab] = useState("details");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preferredAdvisor, setPreferredAdvisor] = useState("");
  const [reason, setReason] = useState("");
  const fileRef = useRef(null);

  const lockedAdvisor =
    (sales || []).find((s) => s.id === user.locked_advisor_id) || null;

  useEffect(() => {
    setError("");
    setNotice("");
  }, [tab]);

  const savePhoto = async (file) => {
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/auth/profile/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(r.data);
      setNotice("Profile photo updated.");
    } catch (x) {
      setError(x.response?.data?.detail || "Could not upload photo");
    } finally {
      setBusy(false);
    }
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const r = await api.patch("/auth/profile", {
        name: f.get("name"),
        phone: f.get("phone"),
        address: f.get("address"),
        gst_number: f.get("gst_number"),
      });
      setUser(r.data);
      setNotice("Details saved.");
    } catch (x) {
      setError(x.response?.data?.detail || "Could not save details");
    } finally {
      setBusy(false);
    }
  };

  const requestChange = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await api.post("/auth/advisor-change", {
        reason,
        preferred_advisor_id: preferredAdvisor,
      });
      setUser({ ...user, advisor_change_requested: true });
      setNotice(
        "Your request has been sent to the host. You'll be notified once it is reviewed.",
      );
      setReason("");
      setPreferredAdvisor("");
    } catch (x) {
      setError(x.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="profile-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="profile-modal"
      >
        <button className="close" onClick={onClose} data-testid="profile-close-button">
          <X />
        </button>
        <div className="profile-head">
          <div className="profile-photo-wrap">
            <Avatar user={user} />
            <button
              type="button"
              className="ghost-btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="profile-photo-button"
            >
              <Camera size={14} /> {user.profile_photo ? "Replace photo" : "Add photo"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => savePhoto(e.target.files?.[0])}
              data-testid="profile-photo-input"
            />
          </div>
          <div>
            <p className="eyebrow">Signed in</p>
            <h2>{user.name}</h2>
            <p className="profile-email">{user.email}</p>
            {lockedAdvisor && (
              <p className="profile-advisor" data-testid="profile-locked-advisor">
                <UserRound size={14} /> Your salesman: <b>{lockedAdvisor.name}</b>
              </p>
            )}
          </div>
        </div>

        <div className="profile-tabs">
          <button
            className={tab === "details" ? "tab active" : "tab"}
            onClick={() => setTab("details")}
            data-testid="profile-tab-details"
          >
            Details
          </button>
          <button
            className={tab === "advisor" ? "tab active" : "tab"}
            onClick={() => setTab("advisor")}
            data-testid="profile-tab-advisor"
          >
            Salesman
          </button>
        </div>

        {tab === "details" && (
          <form className="profile-form" onSubmit={saveDetails}>
            <label>
              Full name
              <input
                name="name"
                defaultValue={user.name || ""}
                required
                data-testid="profile-name-input"
              />
            </label>
            <label>
              Phone number
              <input
                name="phone"
                defaultValue={user.phone || ""}
                placeholder="+91 98765 43210"
                data-testid="profile-phone-input"
              />
            </label>
            <label>
              Delivery address
              <textarea
                name="address"
                defaultValue={user.address || ""}
                placeholder="Where should we send your lights?"
                data-testid="profile-address-input"
              />
            </label>
            <label>
              GST number <small>(optional)</small>
              <input
                name="gst_number"
                defaultValue={user.gst_number || ""}
                placeholder="22AAAAA0000A1Z5"
                data-testid="profile-gst-input"
              />
            </label>
            {error && (
              <div className="error" data-testid="profile-error">
                {error}
              </div>
            )}
            {notice && (
              <div className="notice" data-testid="profile-notice">
                {notice}
              </div>
            )}
            <button
              className="primary-btn full"
              disabled={busy}
              data-testid="profile-save-button"
            >
              {busy ? "Saving…" : "Save details"} <Check size={17} />
            </button>
          </form>
        )}

        {tab === "advisor" && (
          <div className="profile-form">
            {!user.locked_advisor_id && (
              <div className="notice" data-testid="profile-no-advisor">
                <KeyRound size={14} /> You haven't been assigned a salesman yet. Your first
                inquiry will lock you to the salesman you pick.
              </div>
            )}
            {user.locked_advisor_id && user.advisor_change_requested && (
              <div className="notice" data-testid="profile-request-pending">
                <ShieldAlert size={14} /> Your salesman change request is waiting for the
                host to approve.
              </div>
            )}
            {user.locked_advisor_id && !user.advisor_change_requested && (
              <form onSubmit={requestChange}>
                <p className="profile-subtle">
                  Your salesman is locked to <b>{lockedAdvisor?.name || "—"}</b>. To keep
                  commissions fair, only the host can change it. Submit a request below
                  and we'll get back to you.
                </p>
                <label>
                  Preferred salesman <small>(optional)</small>
                  <select
                    value={preferredAdvisor}
                    onChange={(e) => setPreferredAdvisor(e.target.value)}
                    data-testid="profile-preferred-advisor"
                  >
                    <option value="">No preference — the host can pick</option>
                    {(sales || [])
                      .filter((s) => s.id !== user.locked_advisor_id)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} — {s.specialty}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Reason
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    placeholder="Tell the host why you'd like a different salesman…"
                    data-testid="profile-change-reason"
                  />
                </label>
                {error && (
                  <div className="error" data-testid="profile-error">
                    {error}
                  </div>
                )}
                {notice && (
                  <div className="notice" data-testid="profile-notice">
                    {notice}
                  </div>
                )}
                <button
                  className="primary-btn full"
                  disabled={busy}
                  data-testid="profile-request-change-button"
                >
                  {busy ? "Sending…" : "Request salesman change"}{" "}
                  <ArrowRight size={17} />
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { Avatar };
